import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/Seo";
import Hero from "../components/sections/Hero";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import {
  WHY_CHOOSE,
  LOAN_CATEGORIES,
  PROCESS_STEPS,
  FAQS,
} from "../data/site";

const MANIFESTO = [
  { n: "01", title: "Guidance first", body: "We translate complex lending jargon into clear, honest advice tailored to your profile." },
  { n: "02", title: "Choice without limits", body: "One enquiry, many lenders. We surface options from a wide network of Banks and NBFCs." },
  { n: "03", title: "Speed with care", body: "Proactive follow-ups and simple documentation keep your application moving forward." },
];

export default function Home() {
  return (
    <>
      <Seo
        title="CARDED | Fast Loan Solutions Made Simple – Personal, Business & Home Loans"
        description="Apply for Personal, Business, Home Loans, Loan Against Property, Working Capital & Credit Cards through multiple Banks & NBFCs. Fast processing, simple documentation, dedicated support."
      />

      <Hero />

      <Marquee text="PREMIUM FINANCIAL SERVICES" />

      {/* WHY CHOOSE US */}
      <section className="bg-white py-24 sm:py-32" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">
              Why Choose Us
            </span>
            <h2 className="mt-4 max-w-2xl text-4xl font-medium tracking-tight text-[#09090B] sm:text-5xl">
              A partner built for trust and speed.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6C2BD9]/10">
                  <div className="absolute inset-x-0 top-0 h-1 scale-x-0 brand-gradient transition-transform duration-300 group-hover:scale-x-100" />
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#6C2BD9] transition-colors group-hover:brand-gradient group-hover:text-white">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-[#09090B]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOAN CATEGORIES */}
      <section className="border-t border-[#E4E4E7] bg-[#F4F4F5]/40 py-24 sm:py-32" data-testid="loan-categories-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">
              Loan Categories
            </span>
            <h2 className="mt-4 max-w-2xl text-4xl font-medium tracking-tight text-[#09090B] sm:text-5xl">
              Financing for every milestone.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {LOAN_CATEGORIES.map((cat, i) => (
              <Reveal key={cat.slug} delay={i * 0.05}>
                <div
                  className="group flex h-full flex-col rounded-2xl border border-[#E4E4E7] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6C2BD9]/10"
                  data-testid={`category-card-${cat.slug}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#6C2BD9] transition-colors group-hover:brand-gradient group-hover:text-white">
                    <cat.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-[#09090B]">{cat.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-500">{cat.short}</p>
                  <Link
                    to={cat.slug === "credit-card" ? "/credit-cards" : "/loans"}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#6C2BD9] transition-transform hover:gap-2.5"
                    data-testid={`category-learnmore-${cat.slug}`}
                  >
                    Learn More <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-white py-24 sm:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">
              Our Approach
            </span>
          </Reveal>
          <div className="mt-10 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="grid grid-cols-1 items-baseline gap-4 py-10 md:grid-cols-12">
                  <div className="md:col-span-2">
                    <span className="text-2xl font-semibold text-[#A855F7]">{m.n}</span>
                  </div>
                  <h3 className="text-3xl font-medium tracking-tight text-[#09090B] md:col-span-4 sm:text-4xl">
                    {m.title}
                  </h3>
                  <p className="text-base leading-relaxed text-zinc-500 md:col-span-6">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-t border-[#E4E4E7] bg-[#F4F4F5]/40 py-24 sm:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">
              How It Works
            </span>
            <h2 className="mt-4 max-w-2xl text-4xl font-medium tracking-tight text-[#09090B] sm:text-5xl">
              Four simple steps to funding.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-4">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl border border-[#E4E4E7] bg-white p-8">
                  <span className="text-4xl font-bold brand-text">{step.num}</span>
                  <h3 className="mt-4 text-lg font-semibold text-[#09090B]">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{step.desc}</p>
                  {i < PROCESS_STEPS.length - 1 && (
                    <ArrowDown className="absolute -bottom-3 left-1/2 hidden h-6 w-6 -translate-x-1/2 rotate-[-90deg] text-[#A855F7] md:block" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 sm:py-32" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-6 sm:px-10">
          <Reveal className="text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6C2BD9]">
              FAQ
            </span>
            <h2 className="mt-4 text-4xl font-medium tracking-tight text-[#09090B] sm:text-5xl">
              Questions, answered.
            </h2>
          </Reveal>

          <Reveal className="mt-12">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[#E4E4E7]">
                  <AccordionTrigger
                    className="py-5 text-left text-base font-medium text-[#09090B] hover:no-underline sm:text-lg"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-zinc-600 sm:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24 sm:px-10 sm:pb-32" data-testid="cta-section">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl brand-gradient px-8 py-20 text-center sm:px-16">
          <div className="pointer-events-none absolute -top-16 -right-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/80">Need a Loan?</p>
            <h2 className="mx-auto mt-4 max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-6xl">
              Apply Today.
            </h2>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-10 py-4 font-medium text-[#6C2BD9] transition-transform hover:scale-105"
              data-testid="cta-apply-button"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
